(() => {
  if (customElements.get('sooler-hero')) return;

  const MOBILE_QUERY = '(max-width: 1023px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  class SoolerHero extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.abortController = new AbortController();
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      this.slidesContainer = this.querySelector('[data-sooler-hero-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-sooler-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-sooler-hero-dot]'));
      this.previousButton = this.querySelector('[data-sooler-hero-previous]');
      this.nextButton = this.querySelector('[data-sooler-hero-next]');
      this.autoplayButton = this.querySelector('[data-sooler-hero-autoplay-toggle]');
      this.videoButton = this.querySelector('[data-sooler-hero-video-toggle]');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayMilliseconds = Number.parseInt(this.dataset.autoplayMs, 10) || 6000;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.loopSlides = this.dataset.loop === 'true';
      this.transition = this.dataset.transition || 'fade';
      this.animationSpeed = this.dataset.animationSpeed || 'normal';
      this.selectedIndex = 0;
      this.autoplayPausedByUser = false;
      this.autoplayPausedByEditor = false;
      this.videoPausedByUser = false;
      this.flickityAttempts = 0;

      if (!this.slidesContainer || this.slides.length === 0) return;

      this.bindControls();
      this.bindEnvironment();

      if (this.slides.length > 1) {
        this.waitForFlickity();
      } else {
        this.classList.add('is-ready');
        this.updateActiveSlide(0);
      }
    }

    disconnectedCallback() {
      this.connected = false;
      window.clearTimeout(this.flickityTimer);
      this.abortController?.abort();
      this.flickity?.destroy();
      this.flickity = null;
    }

    bindControls() {
      const signal = this.abortController.signal;

      this.previousButton?.addEventListener('click', () => this.selectManually(this.selectedIndex - 1), { signal });
      this.nextButton?.addEventListener('click', () => this.selectManually(this.selectedIndex + 1), { signal });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.selectManually(Number.parseInt(dot.dataset.soolerHeroDot, 10)), { signal });
      });

      this.autoplayButton?.addEventListener('click', () => {
        this.autoplayPausedByUser = !this.autoplayPausedByUser;
        this.updateAutoplayButton();
        this.syncAutoplay();
      }, { signal });

      this.videoButton?.addEventListener('click', () => {
        this.videoPausedByUser = !this.videoPausedByUser;
        this.syncVideos();
      }, { signal });
    }

    bindEnvironment() {
      const signal = this.abortController.signal;

      this.mobileQuery.addEventListener('change', () => this.syncVideos(), { signal });
      this.reducedMotionQuery.addEventListener('change', () => {
        this.syncAutoplay();
        this.syncVideos();
      }, { signal });

      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const slide = event.target.closest('[data-sooler-hero-slide]');
          const index = this.slides.indexOf(slide);
          if (index < 0) return;

          this.autoplayPausedByEditor = true;
          this.syncAutoplay();
          this.flickity?.select(index, false, true);
          if (!this.flickity) this.updateActiveSlide(index);
        }, { signal });

        this.addEventListener('shopify:block:deselect', () => {
          this.autoplayPausedByEditor = false;
          this.syncAutoplay();
        }, { signal });
      }
    }

    waitForFlickity() {
      if (!this.isConnected) return;

      if (window.Flickity) {
        this.initializeCarousel();
        return;
      }

      this.flickityAttempts += 1;
      if (this.flickityAttempts >= 160) {
        this.classList.add('is-static', 'is-ready');
        this.updateActiveSlide(0);
        return;
      }

      this.flickityTimer = window.setTimeout(() => this.waitForFlickity(), 25);
    }

    initializeCarousel() {
      if (this.flickity || !this.slidesContainer) return;

      this.classList.add('is-initializing');
      const canAutoplay = this.autoplayEnabled && !this.reducedMotionQuery.matches && !window.Shopify?.designMode;
      const motion = {
        slow: { selectedAttraction: 0.015, friction: 0.24 },
        normal: { selectedAttraction: 0.025, friction: 0.28 },
        fast: { selectedAttraction: 0.045, friction: 0.34 }
      }[this.animationSpeed] || { selectedAttraction: 0.025, friction: 0.28 };

      this.flickity = new window.Flickity(this.slidesContainer, {
        cellSelector: '[data-sooler-hero-slide]',
        cellAlign: 'left',
        contain: true,
        wrapAround: this.loopSlides,
        prevNextButtons: false,
        pageDots: false,
        setGallerySize: false,
        draggable: this.slides.length > 1,
        rightToLeft: document.dir === 'rtl',
        fade: this.transition === 'fade',
        autoPlay: canAutoplay ? this.autoplayMilliseconds : false,
        pauseAutoPlayOnHover: this.pauseOnHover,
        selectedAttraction: motion.selectedAttraction,
        friction: motion.friction,
        accessibility: true
      });

      this.flickity.on('change', (index) => this.updateActiveSlide(index));
      this.classList.remove('is-initializing');
      this.classList.add('is-ready');
      this.updateActiveSlide(this.flickity.selectedIndex || 0);
      this.updateAutoplayButton();
    }

    selectManually(index) {
      if (!this.flickity) return;

      const lastIndex = this.slides.length - 1;
      let targetIndex = index;
      if (this.loopSlides) {
        targetIndex = (index + this.slides.length) % this.slides.length;
      } else {
        targetIndex = Math.max(0, Math.min(index, lastIndex));
      }

      this.flickity.select(targetIndex);
      this.restartAutoplay();
    }

    updateActiveSlide(index) {
      const previousIndex = this.selectedIndex;
      this.selectedIndex = index;

      this.slides.forEach((slide) => slide.classList.remove('is-exiting'));
      if (previousIndex !== index && this.slides[previousIndex]) {
        this.slides[previousIndex].classList.add('is-exiting');
      }

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          if (isActive) {
            if (element.dataset.soolerHeroPreviousTabindex !== undefined) {
              const previous = element.dataset.soolerHeroPreviousTabindex;
              if (previous === '') element.removeAttribute('tabindex');
              else element.setAttribute('tabindex', previous);
              delete element.dataset.soolerHeroPreviousTabindex;
            }
          } else if (element.dataset.soolerHeroPreviousTabindex === undefined) {
            element.dataset.soolerHeroPreviousTabindex = element.getAttribute('tabindex') || '';
            element.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (!this.loopSlides) {
        if (this.previousButton) this.previousButton.disabled = index === 0;
        if (this.nextButton) this.nextButton.disabled = index === this.slides.length - 1;
      }

      this.syncVideos();
      this.queueNextSlidePreload(index);
    }

    queueNextSlidePreload(index) {
      if (this.slides.length < 2) return;

      let nextIndex = index + 1;
      if (nextIndex >= this.slides.length) {
        if (!this.loopSlides) return;
        nextIndex = 0;
      }

      const nextImage = this.slides[nextIndex]?.querySelector('.sooler-hero__image');
      if (!nextImage || nextImage.dataset.soolerHeroPreloadQueued === 'true') return;
      nextImage.dataset.soolerHeroPreloadQueued = 'true';

      const startPreload = () => {
        if (!this.isConnected) return;
        nextImage.loading = 'eager';
        nextImage.setAttribute('fetchpriority', 'low');
        nextImage.decode?.().catch(() => {});
      };

      const schedulePreload = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(startPreload, { timeout: 1200 });
        } else {
          window.setTimeout(startPreload, 250);
        }
      };

      const currentImage = this.slides[index]?.querySelector('.sooler-hero__image');
      if (!currentImage || currentImage.complete) {
        schedulePreload();
        return;
      }

      currentImage.addEventListener('load', schedulePreload, { once: true, signal: this.abortController.signal });
      currentImage.addEventListener('error', schedulePreload, { once: true, signal: this.abortController.signal });
    }

    restartAutoplay() {
      if (!this.flickity || !this.autoplayEnabled || this.autoplayPausedByUser || this.autoplayPausedByEditor || this.reducedMotionQuery.matches) return;
      this.flickity.stopPlayer();
      this.flickity.playPlayer();
    }

    syncAutoplay() {
      if (!this.flickity || !this.autoplayEnabled) return;

      const shouldPause = this.autoplayPausedByUser || this.autoplayPausedByEditor || this.reducedMotionQuery.matches;
      if (shouldPause) this.flickity.stopPlayer();
      else this.flickity.playPlayer();
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;
      const paused = this.autoplayPausedByUser;
      this.autoplayButton.classList.toggle('is-paused', paused);
      this.autoplayButton.setAttribute('aria-pressed', paused ? 'true' : 'false');
      this.autoplayButton.setAttribute('aria-label', paused ? 'Automatischen Wechsel starten' : 'Automatischen Wechsel pausieren');
    }

    activeVideo() {
      const activeSlide = this.slides[this.selectedIndex];
      if (!activeSlide) return null;
      const device = this.mobileQuery.matches ? 'mobile' : 'desktop';
      return activeSlide.querySelector(`[data-sooler-video="${device}"] video`);
    }

    syncVideos() {
      const currentVideo = this.activeVideo();

      this.querySelectorAll('[data-sooler-video]').forEach((wrapper) => {
        const video = wrapper.querySelector('video');
        if (!video || video === currentVideo) return;
        video.pause();
        wrapper.classList.remove('is-playing');
      });

      if (!currentVideo || this.reducedMotionQuery.matches) {
        currentVideo?.pause();
        currentVideo?.parentElement.classList.remove('is-playing');
        if (this.videoButton) this.videoButton.hidden = true;
        return;
      }

      if (this.videoButton) {
        this.videoButton.hidden = false;
        this.videoButton.textContent = this.videoPausedByUser ? 'Video abspielen' : 'Video pausieren';
        this.videoButton.setAttribute('aria-label', this.videoPausedByUser ? 'Hintergrundvideo abspielen' : 'Hintergrundvideo pausieren');
      }

      if (this.videoPausedByUser) {
        currentVideo.pause();
        currentVideo.parentElement.classList.remove('is-playing');
        return;
      }

      currentVideo.play().then(() => {
        if (this.activeVideo() === currentVideo && !this.videoPausedByUser && !this.reducedMotionQuery.matches) {
          currentVideo.parentElement.classList.add('is-playing');
        }
      }).catch(() => {
        if (this.videoButton) this.videoButton.hidden = true;
      });
    }
  }

  customElements.define('sooler-hero', SoolerHero);
})();
